import { useState } from "react";
import RegionFilters from "./components/RegionFilters";
import RegionAssignmentTable from "./components/RegionAssignmentTable";
import useGroupedRegionData from "./hooks/useGroupedRegionData";
import AssignRegionModal from "./components/AssignRegionModal";
import { Breadcrumbs, Button, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import AddRegionModal from "./components/AddRegionModal";
import AddIcon from "@mui/icons-material/Add";
import { Admin_Regions_API } from "../../../constants";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { Download } from "lucide-react";

const Regions = () => {
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("");

  const [addRegionModalOpen, setAddRegionModalOpen] = useState(false);

  const { data, loading, refetch } = useGroupedRegionData(
    search,
    selectedRegion
  );

  const handleBack = () => {};

  const columns = [
    { headerName: "Employee ID", field: "EmployeeId" },
    { headerName: "Employee Name", field: "EmployeeName" },
    { headerName: "Project", field: "Project" },
    { headerName: "Role", field: "role" },
    { headerName: "Region ID", field: "RegionId" },
    { headerName: "Region Name", field: "RegionName" },
    { headerName: "State ID", field: "StateId" },
    { headerName: "State Name", field: "StateName" },
  ];

  const fetcRegionData = async () => {
    try {
      const response = await axios.get(`${Admin_Regions_API}`);
      const region = response.data.data || [];

      return region; // RETURN data for use in handleExport
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      return null; // Return null or empty array on error
    }
  };

  const handleExport = async () => {
    const data = await fetcRegionData(); // Your function that fetches data
    if (!data || data.length === 0) {
      console.warn("No data to export");
      return;
    }

    // Group data by RegionName
    const groupedData = data.reduce((acc, item) => {
      const region = item.RegionName || "Unknown Region";
      if (!acc[region]) acc[region] = [];
      acc[region].push(item);
      return acc;
    }, {});

    const wb = XLSX.utils.book_new();
    const wsData = [];

    // Prepare headers for employee rows
    const headers = columns.map((col) => col.headerName);

    // Iterate over regions in alphabetical order
    Object.keys(groupedData)
      .sort()
      .forEach((regionName) => {
        // Add Region name as merged header row
        wsData.push([regionName]);

        // Add employee table header
        wsData.push(headers);

        // Add employee data rows
        groupedData[regionName].forEach((employee) => {
          const row = columns.map((col) => {
            let value = employee[col.field];
            // Format dates if any column needs it (adapt if required)
            if (["CreatedAt"].includes(col.field) && value) {
              return dayjs(value).format("DD-MM-YYYY");
            }
            return value || "";
          });
          wsData.push(row);
        });

        // Blank row between groups
        wsData.push([]);
      });

    // After worksheet is built:
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Find all header rows and style them bold
    for (let r = 0; r < wsData.length; r++) {
      const row = wsData[r];
      // If this row equals the header row (matches your header, e.g. columns.map(...))
      if (
        row.length === columns.length &&
        row.every((v, i) => v === columns[i].headerName)
      ) {
        for (let c = 0; c < row.length; c++) {
          const cellAddress = XLSX.utils.encode_cell({ r, c });
          if (ws[cellAddress]) {
            ws[cellAddress].s = { font: { bold: true } };
          }
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, "Region-wise Employee Data");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `region_data_${dayjs().format("YYYYMMDD")}.xlsx`);
  };

  return (
    <>
      <Breadcrumbs separator="›" aria-label="breadcrumb">
        <Link
          underline="hover"
          key="1"
          color="inherit"
          to="/settings"
          onClick={handleBack}
        >
          Admin Panel
        </Link>
        <Typography key="2" color="text.primary">
          Regions
        </Typography>
      </Breadcrumbs>

      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Regions Management</h1>
            <p className="text-sm text-gray-500">
              Manage and view regional assignments
            </p>
          </div>
          <div>
            {/* <Button
              onClick={() => setAddRegionModalOpen(true)}
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              sx={{ mr: 1 }}
            >
              Add New Region
            </Button> */}
            <button
              className="btn btn-primary mr-1"
              onClick={() => setModalOpen(true)}
            >
              Assign New Regions
            </button>
            <Button
              onClick={() => handleExport()}
              variant="contained"
              color="secondary"
              startIcon={<Download />}
              sx={{ mr: 1 }}
            >
              Download
            </Button>
          </div>
        </div>

        {/* Filters */}
        <RegionFilters
          search={search}
          setSearch={setSearch}
          selectedRegion={selectedRegion}
          setSelectedRegion={setSelectedRegion}
        />

        {/* Table */}
        <RegionAssignmentTable
          data={data}
          loading={loading}
          refetch={refetch}
        />

        <AddRegionModal
          isOpen={addRegionModalOpen}
          onClose={() => setAddRegionModalOpen(false)}
        />

        <AssignRegionModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onAssigned={() => {
            refetch();
          }}
        />
      </div>
    </>
  );
};

export default Regions;
